import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Mail, LifeBuoy, Euro, Loader2, LogOut, Link2, CheckCircle2, Upload, Trash2, ExternalLink, MonitorSmartphone, Search } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { id: "panoramica", label: "Panoramica", testid: "admin-tab-overview" },
  { id: "visite", label: "Visite", testid: "admin-tab-analytics" },
  { id: "clienti", label: "Clienti", testid: "admin-tab-clients" },
  { id: "demo", label: "Siti Demo", testid: "admin-tab-demo" },
  { id: "contatti", label: "Contatti", testid: "admin-tab-contacts" },
  { id: "agevolazioni", label: "Agevolazioni", testid: "admin-tab-aid" },
  { id: "assistenza", label: "Assistenza", testid: "admin-tab-support" },
  { id: "blog", label: "Blog", testid: "admin-tab-blog" },
  { id: "chat", label: "Chat AI", testid: "admin-tab-chat" },
  { id: "seo", label: "SEO · Search Console", testid: "admin-tab-seo" },
];

const PLAN_NAMES = {
  business: "Business",
  starter: "Starter",
  growth: "Growth",
  enterprise: "Enterprise",
};

const NEXT_STATUS = { aperta: "in_lavorazione", in_lavorazione: "risolta" };
const STATUS_LABEL = { aperta: "Aperta", in_lavorazione: "In lavorazione", risolta: "Risolta" };

function AdminPageInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("panoramica");
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [demoProgress, setDemoProgress] = useState(null);
  const [demoSearch, setDemoSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [clientPlanFilter, setClientPlanFilter] = useState("all");
  const [clientStatusFilter, setClientStatusFilter] = useState("all");
  const filteredClients = (clients || []).filter((c) => {
    const q = clientSearch.trim().toLowerCase();
    if (q && !c.name.toLowerCase().includes(q) && !c.email.toLowerCase().includes(q)) return false;
    if (clientPlanFilter === "none") { if (c.plan_id) return false; }
    else if (clientPlanFilter !== "all" && c.plan_id !== clientPlanFilter) return false;
    if (clientStatusFilter === "active" && c.subscription_status !== "active") return false;
    if (clientStatusFilter === "cancelled" && c.subscription_status !== "cancelled") return false;
    if (clientStatusFilter === "none" && c.subscription_status) return false;
    return true;
  });
  const [urlInputFor, setUrlInputFor] = useState(null);
  const [urlValue, setUrlValue] = useState("");
  const linkDemoUrl = async (userId) => {
    const url = urlValue.trim();
    if (!url) return;
    try {
      await api.post(`/admin/clients/${userId}/demo-url`, { url });
      toast.success("Demo collegata! Il cliente ha ricevuto un'email.");
      setUrlInputFor(null);
      setUrlValue("");
      refreshClients();
      if (clientDetail?.client?.id === userId) openClient(userId);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const refreshClients = () =>
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
  const demoProgressFor = (userId) => {
    if (!demoProgress) return null;
    if (demoProgress === userId) return 50;
    const [id, pct] = String(demoProgress).split(":");
    return id === userId ? Number(pct) : null;
  };
  const uploadDemo = async (userId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setDemoProgress(userId);
    try {
      await api.post(`/admin/clients/${userId}/demo`, fd, {
        onUploadProgress: (e) => setDemoProgress(e.total ? `${userId}:${Math.round((e.loaded / e.total) * 100)}` : userId),
      });
      toast.success("Sito demo caricato! Il cliente ha ricevuto un'email.");
      refreshClients();
      if (clientDetail?.client?.id === userId) openClient(userId);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDemoProgress(null);
    }
  };
  const deleteDemo = async (userId) => {
    try {
      await api.delete(`/admin/clients/${userId}/demo`);
      toast.success("Sito demo eliminato");
      refreshClients();
      if (clientDetail?.client?.id === userId) openClient(userId);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const openClient = (id) => {
    api.get(`/admin/clients/${id}`).then((r) => setClientDetail(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  const [composer, setComposer] = useState(null);
  const [note, setNote] = useState("");
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [chatSessions, setChatSessions] = useState(null);
  const [chatThread, setChatThread] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const loadAnalytics = () =>
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch((e) => toast.error(formatApiError(e)));

  const loadChat = () =>
    api.get("/admin/chat").then((r) => setChatSessions(r.data)).catch((e) => toast.error(formatApiError(e)));

  const openChat = (sid) => {
    api.get(`/admin/chat/${sid}`).then((r) => setChatThread({ sid, messages: r.data })).catch((e) => toast.error(formatApiError(e)));
  };

  const sendAdminEmail = async () => {
    if (!composer) return;
    try {
      await api.post("/admin/email", { kind: composer.kind, ref_id: composer.refId, note });
      toast.success(`Email inviata a ${composer.label}`);
      setComposer(null);
      setNote("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadPosts = () =>
    api.get("/admin/blog").then((r) => setPosts(r.data)).catch((e) => toast.error(formatApiError(e)));

  const savePost = async () => {
    try {
      if (editing.id) await api.patch(`/admin/blog/${editing.id}`, editing);
      else await api.post("/admin/blog", editing);
      toast.success("Articolo salvato");
      setEditing(null);
      loadPosts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deletePost = async (id) => {
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success("Articolo eliminato");
      setEditing(null);
      loadPosts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const [contacts, setContacts] = useState(null);
  const [aidRequests, setAidRequests] = useState(null);
  const setAidStatus = async (id, status) => {
    try {
      await api.patch(`/admin/aid-requests/${id}/status`, { status });
      setAidRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const deleteAid = async (id) => {
    try {
      await api.delete(`/admin/aid-requests/${id}`);
      setAidRequests((prev) => prev.filter((r) => r.id !== id));
      toast.success("Richiesta eliminata");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const [support, setSupport] = useState(null);
  const [gsc, setGsc] = useState(null);
  const [gscSites, setGscSites] = useState([]);
  const [selProp, setSelProp] = useState("");
  const [selClient, setSelClient] = useState("");

  useEffect(() => {
    if (params.get("gsc") === "connected") {
      toast.success("Account Google collegato con successo!");
      setParams({}, { replace: true });
      setTab("seo");
    }
    if (params.get("gsc") === "error") {
      toast.error("Collegamento Google non riuscito. Riprova.");
      setParams({}, { replace: true });
      setTab("seo");
    }
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/gsc/status").then((r) => setGsc(r.data)).catch(() => {});
  }, []);

  const loadTab = (t) => {
    if ((t === "clienti" || t === "demo") && !clients)
      api.get("/admin/clients").then((r) => setClients(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (t === "contatti" && !contacts)
      api.get("/admin/contacts").then((r) => setContacts(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (t === "agevolazioni" && !aidRequests)
      api.get("/admin/aid-requests").then((r) => setAidRequests(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (t === "assistenza" && !support)
      api.get("/admin/support").then((r) => setSupport(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (t === "visite" && !analytics) loadAnalytics();
    if (t === "chat" && !chatSessions) loadChat();
    if (t === "blog" && !posts) loadPosts();
    if (t === "seo") {
      api.get("/gsc/status").then((r) => {
        setGsc(r.data);
        if (r.data.connected)
          api.get("/gsc/sites").then((s) => setGscSites(s.data)).catch(() => {});
      }).catch(() => {});
      if (!clients)
        api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    }
  };

  useEffect(() => {
    loadTab(tab);
  }, [tab]);

  const cycleSupportStatus = async (req) => {
    const next = NEXT_STATUS[req.status];
    if (!next) return;
    try {
      await api.patch(`/admin/support/${req.id}`, { status: next });
      setSupport((prev) => prev.map((s) => (s.id === req.id ? { ...s, status: next } : s)));
      toast.success(`Richiesta → ${STATUS_LABEL[next]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const assignProperty = async () => {
    if (!selProp || !selClient) return;
    try {
      await api.post("/gsc/assign", { user_id: selClient, property: selProp });
      toast.success("Proprietà Search Console assegnata al cliente");
      setClients((prev) => prev.map((c) => (c.id === selClient ? { ...c, gsc_property: selProp } : c)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const card = "rounded-2xl bg-white border border-black/10 p-6";
  const th = "mono-label text-neutral-500 text-left pb-3";
  const td = "py-3 text-sm text-neutral-700 border-t border-black/5";

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F5]">
      <header className="backdrop-blur-md bg-white/80 border-b border-neutral-200/80 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-brand" className="flex items-center gap-2 font-display font-extrabold text-lg tracking-tight">
            <img src="/lumora-logo.png" alt="Lumora" className="w-7 h-7" />
            LUMORA<span className="text-[#E05638]">.</span>
            <span className="mono-label text-neutral-500 ml-3 hidden sm:inline">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/dashboard" data-testid="admin-client-area-link" className="text-sm text-neutral-600 hover:text-[#111111] transition-colors">
              Area clienti
            </Link>
            <span className="text-sm text-neutral-600 hidden sm:inline">{user?.name}</span>
            <button data-testid="admin-logout-button" onClick={doLogout} className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-[#C8452A] transition-colors">
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <p className="mono-label text-[#E05638]">Pannello riservato</p>
        <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">Gestione studio</h1>

        <div className="mt-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-all duration-300 ${
                tab === t.id ? "bg-[#111111] border-[#111111] text-white" : "bg-white border-black/15 text-neutral-600 hover:border-[#111111]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {composer && (
          <div data-testid="admin-email-composer" className="mt-8 rounded-2xl bg-white border border-black/10 p-6">
            <p className="mono-label text-neutral-500">Email a {composer.label}</p>
            <textarea
              data-testid="admin-email-note-input"
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Scrivi il messaggio per il cliente…"
              className="w-full mt-4 rounded-xl border border-black/15 px-5 py-3.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-neutral-900"
            />
            <div className="flex gap-3 mt-4">
              <button
                data-testid="admin-email-send-button"
                onClick={sendAdminEmail}
                disabled={note.trim().length < 5}
                className="rounded-full bg-[#111111] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#E05638] transition-colors disabled:opacity-50"
              >
                Invia email
              </button>
              <button
                data-testid="admin-email-cancel-button"
                onClick={() => {
                  setComposer(null);
                  setNote("");
                }}
                className="rounded-full border border-black/15 px-6 py-2.5 text-sm font-medium text-neutral-600 hover:border-[#111111] transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        )}

        <motion.div key={tab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-10">
          {tab === "panoramica" && (
            <div data-testid="admin-overview-panel" className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {!overview ? (
                <Loader2 className="animate-spin text-neutral-400" />
              ) : (
                [
                  { icon: Users, label: "Clienti", value: overview.clients },
                  { icon: CheckCircle2, label: "Abbonamenti attivi", value: overview.active_subscriptions },
                  { icon: Mail, label: "Contatti ricevuti", value: overview.contacts },
                  { icon: LifeBuoy, label: "Richieste aperte", value: overview.open_support },
                  { icon: Euro, label: "Incassato (test)", value: `€${overview.revenue}` },
                  { icon: CheckCircle2, label: "Pagamenti PayPal avviati", value: overview.paypal_intents ?? 0 },
                ].map((c) => (
                  <div key={c.label} className={card}>
                    <c.icon size={20} className="text-[#E05638]" />
                    <p className="font-display font-extrabold text-2xl sm:text-3xl mt-4">{c.value}</p>
                    <p className="mono-label text-neutral-500 mt-2">{c.label}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "visite" && (
            <div data-testid="admin-analytics-panel" className="space-y-4">
              {!analytics ? (
                <Loader2 className="animate-spin text-neutral-400" />
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      ["Visite totali", analytics.total],
                      ["Oggi", analytics.today],
                      ["Ultimi 7 giorni", analytics.week],
                      ["Visitatori unici", analytics.unique_visitors],
                    ].map(([label, value]) => (
                      <div key={label} className={card}>
                        <p className="font-display font-extrabold text-2xl sm:text-3xl">{value}</p>
                        <p className="mono-label text-neutral-500 mt-2">{label}</p>
                      </div>
                    ))}
                  </div>
                  <div className={card}>
                    <p className="mono-label text-neutral-500 mb-5">Visite · ultimi 14 giorni</p>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analytics.series} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                          <defs>
                            <linearGradient id="visitsGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#E05638" stopOpacity={0.35} />
                              <stop offset="100%" stopColor="#E05638" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} allowDecimals={false} />
                          <Tooltip />
                          <Area type="monotone" dataKey="views" name="Visite" stroke="#E05638" strokeWidth={2.5} fill="url(#visitsGrad)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="grid lg:grid-cols-2 gap-4">
                    <div className={card}>
                      <p className="mono-label text-neutral-500 mb-5">Pagine più viste</p>
                      {analytics.top_pages.length === 0 ? (
                        <p className="text-sm text-neutral-500">Ancora nessuna visita registrata.</p>
                      ) : (
                        <div className="space-y-3">
                          {analytics.top_pages.map((p) => (
                            <div key={p.path} className="flex items-center justify-between text-sm border-b border-black/5 pb-3">
                              <span className="font-mono text-neutral-700">{p.path}</span>
                              <span className="font-display font-bold">{p.views}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={card} data-testid="traffic-sources-card">
                      <p className="mono-label text-neutral-500 mb-5">Sorgenti di traffico</p>
                      {(!analytics.sources || analytics.sources.length === 0) ? (
                        <p className="text-sm text-neutral-500">Ancora nessuna visita registrata.</p>
                      ) : (
                        <div className="space-y-4">
                          {analytics.sources.map((s) => {
                            const max = analytics.sources[0].count;
                            const pct = Math.max(6, Math.round((s.count / max) * 100));
                            return (
                              <div key={s.source}>
                                <div className="flex items-center justify-between text-sm mb-1.5">
                                  <span className="font-medium text-neutral-700">{s.source}</span>
                                  <span className="font-display font-bold">{s.count}</span>
                                </div>
                                <div className="h-2 rounded-full bg-black/5 overflow-hidden">
                                  <div className="h-full rounded-full bg-[#E05638] transition-all duration-500" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "clienti" && (
            clientDetail ? (
              <div data-testid="admin-client-detail" className="space-y-4">
                <button
                  data-testid="admin-client-back-button"
                  onClick={() => setClientDetail(null)}
                  className="text-sm font-medium text-neutral-600 hover:text-[#E05638] transition-colors"
                >
                  ← Tutti i clienti
                </button>
                <div className={card}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="mono-label text-neutral-500">Cliente</p>
                      <h3 className="font-display font-bold text-2xl mt-2">{clientDetail.client.name}</h3>
                      <p className="text-sm text-neutral-500 mt-1">{clientDetail.client.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display font-bold text-lg">{clientDetail.client.plan_id ? PLAN_NAMES[clientDetail.client.plan_id] : "Nessun piano"}</p>
                      {clientDetail.client.subscription_status === "active" ? (
                        <span className="text-xs font-medium bg-emerald-500/10 text-emerald-600 rounded-full px-3 py-1">Attivo</span>
                      ) : (
                        <span className="text-xs text-neutral-400">Non abbonato</span>
                      )}
                      <button
                        data-testid="admin-client-email-button"
                        onClick={() => setComposer({ kind: "cliente", refId: clientDetail.client.id, label: `${clientDetail.client.name} · ${clientDetail.client.email}` })}
                        className="mt-3 text-xs font-medium rounded-full border border-black/15 px-3 py-1.5 hover:bg-[#111111] hover:text-white transition-colors"
                      >
                        Invia email
                      </button>
                    </div>
                  </div>
                  <p className="mono-label text-neutral-400 mt-5">
                    Cliente dal {clientDetail.client.created_at ? new Date(clientDetail.client.created_at).toLocaleDateString("it-IT") : "—"}
                    {clientDetail.site?.domain ? ` · ${clientDetail.site.domain}` : ""}
                    {clientDetail.site?.gsc_property ? ` · GSC: ${clientDetail.site.gsc_property}` : ""}
                  </p>
                </div>
                <div className={card} data-testid="admin-demo-card">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <MonitorSmartphone size={20} className="text-[#E05638]" />
                      <div>
                        <p className="mono-label text-neutral-500">Sito demo</p>
                        {clientDetail.demo ? (
                          <p className="text-sm mt-1.5" data-testid="admin-demo-info">
                            <span className="font-medium">{clientDetail.demo.original_name}</span>
                            <span className="text-neutral-500"> · {clientDetail.demo.file_count} file · caricato il {new Date(clientDetail.demo.uploaded_at).toLocaleDateString("it-IT")}</span>
                          </p>
                        ) : (
                          <p className="text-sm text-neutral-500 mt-1.5" data-testid="admin-demo-empty">Nessun sito demo caricato per questo cliente.</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {clientDetail.demo && (
                        <>
                          <a
                            data-testid="admin-demo-open-button"
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/demo/${clientDetail.client.id}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-black/15 px-3 py-1.5 hover:bg-[#111111] hover:text-white transition-colors"
                          >
                            Apri demo <ExternalLink size={12} />
                          </a>
                          <button
                            data-testid="admin-demo-copy-button"
                            onClick={() => {
                              navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/demo-shared/${clientDetail.demo.share_token}/`);
                              toast.success("Link pubblico copiato: incollalo dove vuoi, chi lo apre vede la demo senza login.");
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-black/15 px-3 py-1.5 hover:bg-[#111111] hover:text-white transition-colors"
                          >
                            <Link2 size={12} /> Copia link
                          </button>
                          <button
                            data-testid="admin-demo-delete-button"
                            onClick={() => deleteDemo(clientDetail.client.id)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-black/15 px-3 py-1.5 hover:bg-[#E05638] hover:border-[#E05638] hover:text-white transition-colors"
                          >
                            <Trash2 size={12} /> Elimina
                          </button>
                        </>
                      )}
                      <label
                        data-testid="admin-demo-upload-label"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-[#111111] text-white px-4 py-2 cursor-pointer hover:bg-[#E05638] transition-colors"
                      >
                        {demoProgressFor(clientDetail.client.id) !== null ? (
                          <><Loader2 size={12} className="animate-spin" /> {demoProgressFor(clientDetail.client.id)}%</>
                        ) : (
                          <><Upload size={12} /> {clientDetail.demo ? "Sostituisci (.zip)" : "Carica sito (.zip)"}</>
                        )}
                        <input
                          data-testid="admin-demo-file-input"
                          type="file"
                          accept=".zip"
                          className="hidden"
                          disabled={demoProgress !== null}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            if (f) uploadDemo(clientDetail.client.id, f);
                          }}
                        />
                      </label>
                    </div>
                  </div>
                  <p className="text-xs text-neutral-400 mt-3">
                    Carica lo zip del sito statico (con index.html nella radice). Il cliente lo vedrà nel tab "Sito Demo" della sua area.
                  </p>
                </div>
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className={card}>
                    <p className="mono-label text-neutral-500 mb-5">Storico pagamenti</p>
                    {clientDetail.transactions.length === 0 ? (
                      <p className="text-sm text-neutral-500">Nessun pagamento.</p>
                    ) : (
                      <div className="space-y-3">
                        {clientDetail.transactions.map((t) => (
                          <div key={t.session_id} className="flex items-center justify-between text-sm border-b border-black/5 pb-3">
                            <div>
                              <p className="font-medium">{t.plan_name}</p>
                              <p className="text-xs text-neutral-500 mt-0.5">{new Date(t.created_at).toLocaleDateString("it-IT")}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-display font-bold">€{t.amount}</p>
                              <p className={`text-xs mt-0.5 ${t.payment_status === "paid" ? "text-emerald-600" : "text-neutral-500"}`}>
                                {t.payment_status === "paid" ? "Pagato" : t.payment_status}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={card}>
                    <p className="mono-label text-neutral-500 mb-5">Richieste di assistenza</p>
                    {clientDetail.support_requests.length === 0 ? (
                      <p className="text-sm text-neutral-500">Nessuna richiesta.</p>
                    ) : (
                      <div className="space-y-3">
                        {clientDetail.support_requests.map((s, i) => (
                          <div key={i} className="text-sm border-b border-black/5 pb-3">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium">{s.subject}</p>
                              <span className={`text-xs font-medium rounded-full px-2.5 py-0.5 ${
                                s.status === "risolta" ? "bg-emerald-500/10 text-emerald-600" : s.status === "in_lavorazione" ? "bg-amber-500/10 text-amber-600" : "bg-[#E05638]/10 text-[#E05638]"
                              }`}>
                                {STATUS_LABEL[s.status] || s.status}
                              </span>
                            </div>
                            <p className="text-xs text-neutral-500 mt-1.5">{s.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
            <div data-testid="admin-clients-panel" className={`${card} overflow-x-auto`}>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                  <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    data-testid="admin-clients-search-input"
                    type="text"
                    placeholder="Cerca cliente per nome o email…"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="w-full rounded-full border border-black/15 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                  />
                </div>
                <select
                  data-testid="admin-clients-plan-filter"
                  value={clientPlanFilter}
                  onChange={(e) => setClientPlanFilter(e.target.value)}
                  className="rounded-full border border-black/15 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                >
                  <option value="all">Tutti i piani</option>
                  {Object.entries(PLAN_NAMES).map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                  <option value="none">Senza piano</option>
                </select>
                <select
                  data-testid="admin-clients-status-filter"
                  value={clientStatusFilter}
                  onChange={(e) => setClientStatusFilter(e.target.value)}
                  className="rounded-full border border-black/15 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                >
                  <option value="all">Tutti gli stati</option>
                  <option value="active">Abbonamento attivo</option>
                  <option value="cancelled">Disdetto</option>
                  <option value="none">Non abbonato</option>
                </select>
                {(clientSearch || clientPlanFilter !== "all" || clientStatusFilter !== "all") && (
                  <button
                    data-testid="admin-clients-filters-reset"
                    onClick={() => { setClientSearch(""); setClientPlanFilter("all"); setClientStatusFilter("all"); }}
                    className="text-xs font-medium text-neutral-500 hover:text-[#E05638] transition-colors underline underline-offset-2"
                  >
                    Azzera filtri
                  </button>
                )}
              </div>
              {!clients ? (
                <Loader2 className="animate-spin text-neutral-400" />
              ) : filteredClients.length === 0 ? (
                <p data-testid="admin-clients-empty" className="text-sm text-neutral-500 py-6 text-center">
                  {clients.length === 0 ? "Nessun cliente registrato." : "Nessun cliente corrisponde ai filtri selezionati."}
                </p>
              ) : (
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr>
                      <th className={th}>Cliente</th>
                      <th className={th}>Piano</th>
                      <th className={th}>Stato</th>
                      <th className={th}>Search Console</th>
                      <th className={th}>Dal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((c) => (
                      <tr
                        key={c.id}
                        data-testid={`admin-client-row-${c.id}`}
                        onClick={() => openClient(c.id)}
                        className="cursor-pointer hover:bg-[#FAF8F5] transition-colors"
                      >
                        <td className={td}>
                          <p className="font-medium text-[#111111]">{c.name}</p>
                          <p className="text-xs text-neutral-500">{c.email}</p>
                        </td>
                        <td className={td}>{c.plan_id ? PLAN_NAMES[c.plan_id] : "—"}</td>
                        <td className={td}>
                          {c.subscription_status === "active" ? (
                            <span className="text-xs font-medium bg-emerald-500/10 text-emerald-600 rounded-full px-3 py-1">Attivo</span>
                          ) : c.subscription_status === "cancelled" ? (
                            <span className="text-xs font-medium bg-neutral-500/10 text-neutral-500 rounded-full px-3 py-1">Disdetto</span>
                          ) : (
                            <span className="text-xs text-neutral-400">Nessun piano</span>
                          )}
                        </td>
                        <td className={td}>
                          {c.gsc_property ? (
                            <span className="text-xs font-medium text-emerald-600">{c.gsc_property}</span>
                          ) : (
                            <span className="text-xs text-neutral-400">—</span>
                          )}
                        </td>
                        <td className={td}>{c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            )
          )}

          {tab === "demo" && (
            <div data-testid="admin-demo-panel" className={card}>
              <p className="mono-label text-neutral-500">Siti demo dei clienti</p>
              <p className="text-xs text-neutral-400 mt-1.5 mb-6 leading-relaxed">
                Carica lo zip del sito statico (con index.html nella radice): il cliente riceve un'email e trova la demo nel tab "Sito Demo" della sua area.
                Con "Copia link" ottieni un link pubblico da inviare direttamente (WhatsApp, email): chi lo apre vede solo la demo, senza login.
              </p>
              <div className="relative mb-5 max-w-md">
                <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="admin-demo-search-input"
                  type="text"
                  placeholder="Cerca cliente per nome o email…"
                  value={demoSearch}
                  onChange={(e) => setDemoSearch(e.target.value)}
                  className="w-full rounded-full border border-black/15 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                />
              </div>
              {!clients ? (
                <Loader2 className="animate-spin text-neutral-400" />
              ) : clients.length === 0 ? (
                <p className="text-sm text-neutral-500">Nessun cliente registrato.</p>
              ) : (
                <div className="space-y-3">
                  {clients
                    .filter((c) => {
                      const q = demoSearch.trim().toLowerCase();
                      return !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
                    })
                    .map((c) => (
                    <div key={c.id} data-testid={`admin-demo-row-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-black/10 px-5 py-4">
                      <div className="min-w-[220px]">
                        <p className="font-medium text-[#111111]">{c.name}</p>
                        <p className="text-xs text-neutral-500">{c.email}</p>
                        {c.demo ? (
                          <p className="text-xs text-emerald-600 mt-1.5">
                            {c.demo.external_url
                              ? <>Demo: link esterno · {new Date(c.demo.uploaded_at).toLocaleDateString("it-IT")}</>
                              : <>Demo: {c.demo.original_name} · {c.demo.file_count} file · {new Date(c.demo.uploaded_at).toLocaleDateString("it-IT")}</>}
                          </p>
                        ) : (
                          <p className="text-xs text-neutral-400 mt-1.5">Nessuna demo caricata</p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {c.demo && (
                          <>
                            <a
                              data-testid={`admin-demo-open-${c.id}`}
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/demo/${c.id}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-black/15 px-3 py-1.5 hover:bg-[#111111] hover:text-white transition-colors"
                            >
                              Apri <ExternalLink size={12} />
                            </a>
                            <button
                              data-testid={`admin-demo-copy-${c.id}`}
                              onClick={() => {
                                navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/demo-shared/${c.demo.share_token}/`);
                                toast.success("Link pubblico copiato: incollalo dove vuoi, chi lo apre vede la demo senza login.");
                              }}
                              className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-black/15 px-3 py-1.5 hover:bg-[#111111] hover:text-white transition-colors"
                            >
                              <Link2 size={12} /> Copia link
                            </button>
                            <button
                              data-testid={`admin-demo-delete-${c.id}`}
                              onClick={() => deleteDemo(c.id)}
                              className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-black/15 px-3 py-1.5 hover:bg-[#E05638] hover:border-[#E05638] hover:text-white transition-colors"
                            >
                              <Trash2 size={12} /> Elimina
                            </button>
                          </>
                        )}
                        <label
                          data-testid={`admin-demo-upload-${c.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-[#111111] text-white px-4 py-2 cursor-pointer hover:bg-[#E05638] transition-colors"
                        >
                          {demoProgressFor(c.id) !== null ? (
                            <><Loader2 size={12} className="animate-spin" /> {demoProgressFor(c.id)}%</>
                          ) : (
                            <><Upload size={12} /> {c.demo && !c.demo.external_url ? "Sostituisci" : "Carica .zip"}</>
                          )}
                          <input
                            data-testid={`admin-demo-file-input-${c.id}`}
                            type="file"
                            accept=".zip"
                            className="hidden"
                            disabled={demoProgress !== null}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = "";
                              if (f) uploadDemo(c.id, f);
                            }}
                          />
                        </label>
                        <button
                          data-testid={`admin-demo-url-button-${c.id}`}
                          onClick={() => {
                            setUrlInputFor(urlInputFor === c.id ? null : c.id);
                            setUrlValue(c.demo?.external_url || "");
                          }}
                          className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-black/15 px-3 py-1.5 hover:bg-[#111111] hover:text-white transition-colors"
                        >
                          <Link2 size={12} /> Collega URL
                        </button>
                        {urlInputFor === c.id && (
                          <div className="flex items-center gap-2 w-full mt-1">
                            <input
                              data-testid={`admin-demo-url-input-${c.id}`}
                              type="url"
                              placeholder="https://… incolla qui il link della demo"
                              value={urlValue}
                              onChange={(e) => setUrlValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") linkDemoUrl(c.id); }}
                              className="flex-1 rounded-full border border-black/15 bg-white px-4 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-neutral-900"
                            />
                            <button
                              data-testid={`admin-demo-url-confirm-${c.id}`}
                              onClick={() => linkDemoUrl(c.id)}
                              className="text-xs font-semibold rounded-full bg-[#E05638] text-white px-4 py-2 hover:bg-[#C8452A] transition-colors"
                            >
                              Collega
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {clients.length > 0 &&
                    clients.filter((c) => {
                      const q = demoSearch.trim().toLowerCase();
                      return !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
                    }).length === 0 && (
                      <p data-testid="admin-demo-search-empty" className="text-sm text-neutral-500 py-4 text-center">
                        Nessun cliente corrisponde a "{demoSearch}".
                      </p>
                    )}
                </div>
              )}
            </div>
          )}

          {tab === "contatti" && (
            <div data-testid="admin-contacts-panel" className="space-y-4">
              {!contacts ? (
                <Loader2 className="animate-spin text-neutral-400" />
              ) : contacts.length === 0 ? (
                <p className="text-sm text-neutral-500">Nessun contatto ricevuto.</p>
              ) : (
                contacts.map((c, i) => (
                  <div key={i} data-testid={`admin-contact-${i}`} className={card}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <p className="font-medium">{c.name} <span className="text-neutral-500 text-sm">· {c.email}</span></p>
                      <div className="flex items-center gap-3">
                        <p className="mono-label text-neutral-400">{new Date(c.created_at).toLocaleDateString("it-IT")}</p>
                        <button
                          data-testid={`admin-contact-reply-${i}`}
                          onClick={() => setComposer({ kind: "contatto", refId: c.id, label: `${c.name} · ${c.email}` })}
                          className="text-xs font-medium rounded-full border border-black/15 px-3 py-1 hover:bg-[#111111] hover:text-white transition-colors"
                        >
                          Rispondi
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600 leading-relaxed">{c.message}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "agevolazioni" && (
            <div data-testid="admin-aid-panel" className="space-y-4">
              {!aidRequests ? (
                <Loader2 className="animate-spin text-neutral-400" />
              ) : aidRequests.length === 0 ? (
                <p className="text-sm text-neutral-500">Nessuna richiesta di agevolazione ricevuta.</p>
              ) : (
                aidRequests.map((r) => (
                  <div key={r.id} data-testid={`admin-aid-${r.id}`} className={card}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="font-medium">
                          {r.business_name}
                          <span className="text-neutral-500 text-sm"> · {r.name} · {r.email}</span>
                        </p>
                        <p className="mono-label text-neutral-400 mt-1.5">
                          {r.activity_type} · {new Date(r.created_at).toLocaleDateString("it-IT")}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          data-testid={`admin-aid-status-${r.id}`}
                          className={`text-xs font-medium rounded-full px-3 py-1 ${
                            r.status === "approvata"
                              ? "bg-emerald-500/10 text-emerald-600"
                              : r.status === "contattata"
                                ? "bg-amber-500/10 text-amber-600"
                                : r.status === "rifiutata"
                                  ? "bg-neutral-500/10 text-neutral-500"
                                  : "bg-[#E05638]/10 text-[#E05638]"
                          }`}
                        >
                          {{ nuova: "Nuova", contattata: "Contattata", approvata: "Approvata", rifiutata: "Rifiutata" }[r.status] || r.status}
                        </span>
                        <button
                          data-testid={`admin-aid-reply-${r.id}`}
                          onClick={() => setComposer({ kind: "agevolazione", refId: r.id, label: `${r.business_name} · ${r.email}` })}
                          className="text-xs font-medium rounded-full border border-black/15 px-3 py-1 hover:bg-[#111111] hover:text-white transition-colors"
                        >
                          Rispondi
                        </button>
                        {r.status === "nuova" && (
                          <button
                            data-testid={`admin-aid-contact-${r.id}`}
                            onClick={() => setAidStatus(r.id, "contattata")}
                            className="text-xs font-medium rounded-full border border-black/15 px-3 py-1 hover:bg-[#111111] hover:text-white transition-colors"
                          >
                            → Contattata
                          </button>
                        )}
                        {(r.status === "nuova" || r.status === "contattata") && (
                          <>
                            <button
                              data-testid={`admin-aid-approve-${r.id}`}
                              onClick={() => setAidStatus(r.id, "approvata")}
                              className="text-xs font-medium rounded-full border border-emerald-500/40 text-emerald-600 px-3 py-1 hover:bg-emerald-600 hover:text-white transition-colors"
                            >
                              → Approvata
                            </button>
                            <button
                              data-testid={`admin-aid-reject-${r.id}`}
                              onClick={() => setAidStatus(r.id, "rifiutata")}
                              className="text-xs font-medium rounded-full border border-black/15 px-3 py-1 hover:bg-[#E05638] hover:border-[#E05638] hover:text-white transition-colors"
                            >
                              Rifiuta
                            </button>
                          </>
                        )}
                        {r.status === "rifiutata" && (
                          <button
                            data-testid={`admin-aid-reopen-${r.id}`}
                            onClick={() => setAidStatus(r.id, "nuova")}
                            className="text-xs font-medium rounded-full border border-black/15 px-3 py-1 hover:bg-[#111111] hover:text-white transition-colors"
                          >
                            Riapri
                          </button>
                        )}
                        <button
                          data-testid={`admin-aid-delete-${r.id}`}
                          onClick={() => deleteAid(r.id)}
                          className="text-xs font-medium rounded-full border border-black/15 px-3 py-1 hover:bg-[#E05638] hover:border-[#E05638] hover:text-white transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600 leading-relaxed">{r.message}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "assistenza" && (
            <div data-testid="admin-support-panel" className="space-y-4">
              {!support ? (
                <Loader2 className="animate-spin text-neutral-400" />
              ) : support.length === 0 ? (
                <p className="text-sm text-neutral-500">Nessuna richiesta di assistenza.</p>
              ) : (
                support.map((s) => (
                  <div key={s.id} data-testid={`admin-support-${s.id}`} className={card}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <p className="font-medium">{s.subject}</p>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-medium rounded-full px-3 py-1 ${
                          s.status === "risolta" ? "bg-emerald-500/10 text-emerald-600" : s.status === "in_lavorazione" ? "bg-amber-500/10 text-amber-600" : "bg-[#E05638]/10 text-[#E05638]"
                        }`}>
                          {STATUS_LABEL[s.status] || s.status}
                        </span>
                        <button
                          data-testid={`admin-support-reply-${s.id}`}
                          onClick={() => setComposer({ kind: "assistenza", refId: s.id, label: s.email })}
                          className="text-xs font-medium rounded-full border border-black/15 px-3 py-1 hover:bg-[#111111] hover:text-white transition-colors"
                        >
                          Rispondi
                        </button>
                        {NEXT_STATUS[s.status] && (
                          <button
                            data-testid={`admin-support-advance-${s.id}`}
                            onClick={() => cycleSupportStatus(s)}
                            className="text-xs font-medium rounded-full border border-black/15 px-3 py-1 hover:bg-[#111111] hover:text-white transition-colors"
                          >
                            → {STATUS_LABEL[NEXT_STATUS[s.status]]}
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600 leading-relaxed">{s.message}</p>
                    <p className="mono-label text-neutral-400 mt-3">{s.email} · {new Date(s.created_at).toLocaleDateString("it-IT")}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "blog" && (
            <div data-testid="admin-blog-panel" className="space-y-4">
              {editing ? (
                <div className={card}>
                  <p className="mono-label text-neutral-500 mb-6">{editing.id ? "Modifica articolo" : "Nuovo articolo"}</p>
                  <div className="space-y-4">
                    <input
                      data-testid="blog-editor-title"
                      placeholder="Titolo articolo"
                      value={editing.title}
                      onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                      className="w-full rounded-xl border border-black/15 px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    />
                    <input
                      data-testid="blog-editor-slug"
                      placeholder="Slug (opzionale, generato dal titolo)"
                      value={editing.slug}
                      onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                      className="w-full rounded-xl border border-black/15 px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    />
                    <input
                      data-testid="blog-editor-excerpt"
                      placeholder="Sottotitolo / anteprima"
                      value={editing.excerpt}
                      onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                      className="w-full rounded-xl border border-black/15 px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    />
                    <input
                      data-testid="blog-editor-cover"
                      placeholder="URL immagine di copertina (opzionale)"
                      value={editing.cover}
                      onChange={(e) => setEditing({ ...editing, cover: e.target.value })}
                      className="w-full rounded-xl border border-black/15 px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    />
                    <textarea
                      data-testid="blog-editor-content"
                      rows={12}
                      placeholder="Contenuto. Paragrafi separati da riga vuota. Usa '## Titolo' per i sottotitoli."
                      value={editing.content}
                      onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                      className="w-full rounded-xl border border-black/15 px-5 py-3.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-neutral-900 font-mono"
                    />
                    <label className="flex items-center gap-3 text-sm text-neutral-700 cursor-pointer">
                      <input
                        data-testid="blog-editor-published"
                        type="checkbox"
                        checked={editing.published}
                        onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                        className="w-4 h-4 accent-[#E05638]"
                      />
                      Pubblicato (visibile sul blog)
                    </label>
                    <div className="flex flex-wrap gap-3 pt-2">
                      <button
                        data-testid="blog-editor-save"
                        onClick={savePost}
                        disabled={!editing.title.trim()}
                        className="rounded-full bg-[#111111] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#E05638] transition-colors disabled:opacity-50"
                      >
                        Salva articolo
                      </button>
                      <button
                        data-testid="blog-editor-cancel"
                        onClick={() => setEditing(null)}
                        className="rounded-full border border-black/15 px-6 py-2.5 text-sm font-medium text-neutral-600 hover:border-[#111111] transition-colors"
                      >
                        Annulla
                      </button>
                      {editing.id && (
                        <button
                          data-testid="blog-editor-delete"
                          onClick={() => deletePost(editing.id)}
                          className="rounded-full border border-[#E05638]/40 text-[#C8452A] px-6 py-2.5 text-sm font-medium hover:bg-[#E05638] hover:text-white transition-colors"
                        >
                          Elimina
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    data-testid="blog-new-post-button"
                    onClick={() => setEditing({ title: "", slug: "", excerpt: "", cover: "", content: "", published: false })}
                    className="rounded-full bg-[#111111] text-white px-6 py-3 text-sm font-semibold hover:bg-[#E05638] transition-colors"
                  >
                    + Nuovo articolo
                  </button>
                  {!posts ? (
                    <Loader2 className="animate-spin text-neutral-400" />
                  ) : posts.length === 0 ? (
                    <p className="text-sm text-neutral-500">Nessun articolo. Scrivi il primo!</p>
                  ) : (
                    posts.map((p) => (
                      <div key={p.id} data-testid={`admin-blog-row-${p.slug}`} className={`${card} flex flex-wrap items-center justify-between gap-4`}>
                        <div>
                          <div className="flex items-center gap-3">
                            <p className="font-medium">{p.title}</p>
                            <span className={`text-xs font-medium rounded-full px-3 py-1 ${p.published ? "bg-emerald-500/10 text-emerald-600" : "bg-black/5 text-neutral-500"}`}>
                              {p.published ? "Pubblicato" : "Bozza"}
                            </span>
                          </div>
                          <p className="mono-label text-neutral-400 mt-2">
                            /blog/{p.slug} · {new Date(p.created_at).toLocaleDateString("it-IT")}
                          </p>
                        </div>
                        <button
                          data-testid={`admin-blog-edit-${p.slug}`}
                          onClick={() => setEditing({ ...p })}
                          className="text-xs font-medium rounded-full border border-black/15 px-4 py-2 hover:bg-[#111111] hover:text-white transition-colors"
                        >
                          Modifica
                        </button>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          )}

          {tab === "chat" && (
            <div data-testid="admin-chat-panel" className="space-y-4">
              {chatThread ? (
                <div className={card}>
                  <button
                    data-testid="admin-chat-back-button"
                    onClick={() => setChatThread(null)}
                    className="text-sm font-medium text-neutral-600 hover:text-[#E05638] transition-colors"
                  >
                    ← Tutte le conversazioni
                  </button>
                  <p className="mono-label text-neutral-400 mt-4 mb-5">Sessione {chatThread.sid.slice(0, 8)}…</p>
                  <div className="space-y-3">
                    {chatThread.messages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          m.role === "user" ? "bg-[#111111] text-white" : "bg-[#F3F0EA] text-neutral-800"
                        }`}>
                          {m.text}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : !chatSessions ? (
                <Loader2 className="animate-spin text-neutral-400" />
              ) : chatSessions.length === 0 ? (
                <p className="text-sm text-neutral-500">Nessuna conversazione ancora.</p>
              ) : (
                chatSessions.map((s) => (
                  <button
                    key={s.session_id}
                    data-testid={`admin-chat-session-${s.session_id.slice(0, 8)}`}
                    onClick={() => openChat(s.session_id)}
                    className={`${card} w-full text-left hover:border-[#111111] transition-colors`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm font-medium truncate">{s.first_message}</p>
                      <span className="mono-label text-neutral-400 shrink-0">{s.count} msg</span>
                    </div>
                    <p className="mono-label text-neutral-400 mt-2">
                      {s.last_at ? new Date(s.last_at).toLocaleString("it-IT") : ""}
                    </p>
                  </button>
                ))
              )}
            </div>
          )}

          {tab === "seo" && (
            <div data-testid="admin-seo-panel" className="space-y-4">
              {!gsc ? (
                <Loader2 className="animate-spin text-neutral-400" />
              ) : !gsc.configured ? (
                <div className={card}>
                  <p className="mono-label text-[#E05638] mb-3">Configurazione richiesta</p>
                  <h3 className="font-display font-bold text-xl">Collega Google Search Console</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed mt-3 max-w-lg">
                    Per mostrare dati SEO reali servono le credenziali OAuth di Google Cloud
                    (Client ID e Client Secret con API Search Console attiva). Forniscile e
                    completerò la configurazione.
                  </p>
                </div>
              ) : !gsc.connected ? (
                <div className={card}>
                  <h3 className="font-display font-bold text-xl">Collega il tuo account Google</h3>
                  <p className="text-sm text-neutral-600 mt-3 max-w-lg">
                    Autorizza l'accesso in sola lettura a Search Console per importare keyword, clic e impressioni dei siti dei clienti.
                  </p>
                  <a
                    data-testid="gsc-connect-button"
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/gsc/auth/google`}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#111111] text-white px-6 py-3 text-sm font-semibold hover:bg-[#E05638] transition-colors"
                  >
                    <Link2 size={15} /> Collega Google
                  </a>
                  <div className="mt-6 rounded-xl bg-[#F3F0EA] border border-black/10 p-4">
                    <p className="mono-label text-neutral-500 mb-2">URI da copiare in Google Cloud → Credenziali → URI di reindirizzamento autorizzati</p>
                    <div className="flex items-center gap-2">
                      <code data-testid="gsc-redirect-uri" className="text-xs flex-1 break-all text-neutral-700">
                        {`${process.env.REACT_APP_BACKEND_URL}/api/gsc/auth/google/callback`}
                      </code>
                      <button
                        data-testid="gsc-copy-uri-button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/gsc/auth/google/callback`);
                          toast.success("URI copiato negli appunti!");
                        }}
                        className="shrink-0 text-xs font-medium rounded-full bg-[#111111] text-white px-4 py-2 hover:bg-[#E05638] transition-colors"
                      >
                        Copia
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={card}>
                  <div className="flex items-center gap-3 mb-6">
                    <CheckCircle2 size={18} className="text-emerald-500" />
                    <p className="font-medium">Account Google collegato</p>
                  </div>
                  <p className="mono-label text-neutral-500 mb-4">Assegna una proprietà a un cliente</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <select
                      data-testid="gsc-property-select"
                      value={selProp}
                      onChange={(e) => setSelProp(e.target.value)}
                      className="flex-1 rounded-xl border border-black/15 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    >
                      <option value="">Seleziona proprietà…</option>
                      {gscSites.map((s) => (
                        <option key={s.url} value={s.url}>{s.url}</option>
                      ))}
                    </select>
                    <select
                      data-testid="gsc-client-select"
                      value={selClient}
                      onChange={(e) => setSelClient(e.target.value)}
                      className="flex-1 rounded-xl border border-black/15 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    >
                      <option value="">Seleziona cliente…</option>
                      {(clients || []).filter((c) => c.subscription_status === "active").map((c) => (
                        <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                      ))}
                    </select>
                    <button
                      data-testid="gsc-assign-button"
                      onClick={assignProperty}
                      disabled={!selProp || !selClient}
                      className="rounded-full bg-[#E05638] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C8452A] transition-colors disabled:opacity-50"
                    >
                      Assegna
                    </button>
                  </div>
                  {(clients || []).some((c) => c.gsc_property) && (
                    <div className="mt-6 space-y-2">
                      {clients.filter((c) => c.gsc_property).map((c) => (
                        <p key={c.id} className="text-xs text-neutral-500">
                          <span className="font-medium text-neutral-700">{c.name}</span> → {c.gsc_property}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user === false) navigate("/accedi");
    else if (user && user.role !== "admin") navigate("/dashboard");
  }, [user]);
  if (!user || user.role !== "admin")
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF8F5]">
        <span className="mono-label text-neutral-500">Caricamento…</span>
      </div>
    );
  return <AdminPageInner />;
}
